import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonEmails.Actions';
import { PersonEmailState, PersonEmailUnloadState } from '../states/PersonEmailState';
import { EmailViewModel } from '../../models/Entities/Person/Email/EmailModels';

export type KnownAction =
    | Actions.PersonEmails.PersonEmailGetAllRequestAction
    | Actions.PersonEmails.PersonEmailGetAllSuccessAction
    | Actions.PersonEmails.PersonEmailGetAllFailureAction
    | Actions.PersonEmails.PersonEmailGetByIdRequestAction
    | Actions.PersonEmails.PersonEmailGetByIdSuccessAction
    | Actions.PersonEmails.PersonEmailGetByIdFailureAction
    | Actions.PersonEmails.PersonEmailCreateRequestAction
    | Actions.PersonEmails.PersonEmailCreateSuccessAction
    | Actions.PersonEmails.PersonEmailCreateFailureAction
    | Actions.PersonEmails.PersonEmailUpdateRequestAction
    | Actions.PersonEmails.PersonEmailUpdateSuccessAction
    | Actions.PersonEmails.PersonEmailUpdateFailureAction
    | Actions.PersonEmails.PersonEmailDeleteRequestAction
    | Actions.PersonEmails.PersonEmailDeleteSuccessAction
    | Actions.PersonEmails.PersonEmailDeleteFailureAction;

export const PersonEmailsReducer: Reducer<PersonEmailState> = (
    state: PersonEmailState | undefined,
    incomingAction: Action
): PersonEmailState => {
    if (state === undefined) {
        return PersonEmailUnloadState as PersonEmailState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_EMAIL_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_EMAIL_GETALL_SUCCESS: {
            const otherPersons = state.list.filter((item: EmailViewModel) => item.personId !== action.personId);
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: [...otherPersons, ...action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_EMAIL_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_EMAIL_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EMAIL_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: EmailViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_EMAIL_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_EMAIL_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EMAIL_CREATE_SUCCESS: {
            const createdList = state.list.some((item: EmailViewModel) => item.id === action.payload.id)
                ? state.list.map((item: EmailViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_EMAIL_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_EMAIL_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EMAIL_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: EmailViewModel) => item.id === action.payload.id)
                ? state.list.map((item: EmailViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_EMAIL_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_EMAIL_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EMAIL_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: EmailViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_EMAIL_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        default:
            return state;
    }
};
